import type { ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage, cn } from '@vibesites/ui-primitives';
import { btnPrimary, hasValue, linkTarget } from '../utils/content';

const photoOneDef: SlotGroupDefinition = {
  label: 'Story 1 Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[17].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[17].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 17, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A photo that illustrates story 1 (sustainability, building design, or the landscape around the property).',
    context: 'Row 1 of 3 alternating photo/text stories on the Impact page. Different photo from rows 2 and 3.',
  },
};
const photoTwoDef: SlotGroupDefinition = {
  label: 'Story 2 Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[18].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[18].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 18, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A photo that illustrates story 2 (community life, outdoor spaces, or programs for residents).',
    context: 'Row 2 of 3 alternating photo/text stories on the Impact page. Different photo from rows 1 and 3.',
  },
};
const photoThreeDef: SlotGroupDefinition = {
  label: 'Story 3 Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[19].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[19].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 19, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A photo that illustrates story 3 (art, local partnerships, or the neighborhood).',
    context: 'Row 3 of 3 alternating photo/text stories on the Impact page. Different photo from rows 1 and 2.',
  },
};

const ctaOneDef: SlotGroupDefinition = {
  label: 'Story 1 Button',
  description: 'Hidden while the URL is empty.',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/features' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Learn More' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};
const ctaTwoDef: SlotGroupDefinition = {
  label: 'Story 2 Button',
  description: 'Hidden while the URL is empty.',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Explore' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};
const ctaThreeDef: SlotGroupDefinition = {
  label: 'Story 3 Button',
  description: 'Hidden while the URL is empty.',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'See Here' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

interface RowProps {
  index: number;
  heading: ReactNode;
  body: ReactNode;
  photo: ReactNode;
  cta: ReactNode;
}

function Row({ index, heading, body, photo, cta }: RowProps) {
  const reverse = index % 2 === 1;
  return (
    <div className={cn('grid items-center gap-8 md:grid-cols-2 lg:gap-16', index > 0 && 'mt-16 md:mt-24')}>
      <div className={cn(reverse && 'md:order-2')}>{photo}</div>
      <div className={cn(reverse ? 'js-reveal-left md:order-1' : 'js-reveal-right')}>
        {heading}
        {body}
        {cta}
      </div>
    </div>
  );
}

export function StoryRows() {
  const h1 = useContentToken('storyrows.heading_1');
  const h2 = useContentToken('storyrows.heading_2');
  const h3 = useContentToken('storyrows.heading_3');
  const p1 = useSlotGroup('storyrows.photo_1', photoOneDef);
  const p2 = useSlotGroup('storyrows.photo_2', photoTwoDef);
  const p3 = useSlotGroup('storyrows.photo_3', photoThreeDef);
  const c1 = useSlotGroup('storyrows.cta_1', ctaOneDef);
  const c2 = useSlotGroup('storyrows.cta_2', ctaTwoDef);
  const c3 = useSlotGroup('storyrows.cta_3', ctaThreeDef);

  const headingClass = 'js-heading text-[28px] text-secondary sm:text-[34px]';
  const bodyClass = 'js-copy mt-5 text-foreground';
  const img = (p: Record<string, string | null>) => (
    <div className="js-reveal-img js-zoom aspect-[4/3] w-full overflow-hidden bg-muted">
      <VibesitesImage url={p.image || ''} alt={hasValue(p.alt) ? p.alt : ''} sizes="(min-width: 768px) 50vw, 100vw" className="h-full w-full object-cover" />
    </div>
  );
  const button = (c: Record<string, string | null>) =>
    hasValue(c.url) ? (
      <a href={c.url || '#'} target={linkTarget(c.url, c._target)} className={`${btnPrimary} mt-8`}>
        {c.label}
      </a>
    ) : (
      <span />
    );

  return (
    <section className="bg-background py-12 md:py-16">
      <div className="mx-auto max-w-[1180px] px-6 lg:px-10">
        <Row
          index={0}
          heading={<EditableToken path="storyrows.heading_1" as="h2" className={headingClass}>{h1}</EditableToken>}
          body={<RichTextContent path="storyrows.body_1" as="div" className={bodyClass} />}
          photo={<EditableSlotGroup groupPath="storyrows.photo_1" definition={photoOneDef}>{img(p1)}</EditableSlotGroup>}
          cta={<EditableSlotGroup groupPath="storyrows.cta_1" definition={ctaOneDef}>{button(c1)}</EditableSlotGroup>}
        />
        <Row
          index={1}
          heading={<EditableToken path="storyrows.heading_2" as="h2" className={headingClass}>{h2}</EditableToken>}
          body={<RichTextContent path="storyrows.body_2" as="div" className={bodyClass} />}
          photo={<EditableSlotGroup groupPath="storyrows.photo_2" definition={photoTwoDef}>{img(p2)}</EditableSlotGroup>}
          cta={<EditableSlotGroup groupPath="storyrows.cta_2" definition={ctaTwoDef}>{button(c2)}</EditableSlotGroup>}
        />
        <Row
          index={2}
          heading={<EditableToken path="storyrows.heading_3" as="h2" className={headingClass}>{h3}</EditableToken>}
          body={<RichTextContent path="storyrows.body_3" as="div" className={bodyClass} />}
          photo={<EditableSlotGroup groupPath="storyrows.photo_3" definition={photoThreeDef}>{img(p3)}</EditableSlotGroup>}
          cta={<EditableSlotGroup groupPath="storyrows.cta_3" definition={ctaThreeDef}>{button(c3)}</EditableSlotGroup>}
        />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'StoryRows',
  displayName: 'Story Rows',
  tokens: {
    'storyrows.heading_1': {
      label: 'Story 1 Heading',
      type: 'text',
      defaultValue: 'Built With Care',
      generator: {
        prompt: 'Headline for this property’s strongest sustainability, design, or building-quality story.',
        context: 'Row 1 of 3 on the Impact page. Do not overlap rows 2 and 3.',
        length: '2–5 words',
      },
    },
    'storyrows.body_1': {
      label: 'Story 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>From efficient systems to thoughtful materials, <span data-type="field-token" data-field="property_name"></span> is designed to be comfortable for residents and considerate of the world around it.</p>',
      generator: {
        prompt: 'Explain story 1 with concrete, verifiable details about this property. Leave empty if nothing is on record.',
        context: 'Body of row 1 on the Impact page.',
        length: '2–4 sentences',
      },
    },
    'storyrows.heading_2': {
      label: 'Story 2 Heading',
      type: 'text',
      defaultValue: 'Rooted in Community',
      generator: {
        prompt: 'Headline for a community, outdoor-space, or resident-program story at this property.',
        context: 'Row 2 of 3 on the Impact page. Do not overlap rows 1 and 3.',
        length: '2–5 words',
      },
    },
    'storyrows.body_2': {
      label: 'Story 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Shared spaces, outdoor gathering areas, and resident events make it easy to meet neighbors and feel at home.</p>',
      generator: {
        prompt: 'Explain story 2 with concrete details about this property’s spaces or programs.',
        context: 'Body of row 2 on the Impact page.',
        length: '2–4 sentences',
      },
    },
    'storyrows.heading_3': {
      label: 'Story 3 Heading',
      type: 'text',
      defaultValue: 'A Sense of Place',
      generator: {
        prompt: 'Headline for a local art, partnership, or neighborhood-connection story at this property.',
        context: 'Row 3 of 3 on the Impact page. Do not overlap rows 1 and 2.',
        length: '2–5 words',
      },
    },
    'storyrows.body_3': {
      label: 'Story 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Local character shows up everywhere, from the design details at home to the shops, trails, and favorite spots just down the street in <span data-type="field-token" data-field="property_address_city"></span>.</p>',
      generator: {
        prompt: 'Explain story 3 with concrete, hyperlocal details.',
        context: 'Body of row 3 on the Impact page.',
        length: '2–4 sentences',
      },
    },
  },
  slotGroups: {
    'storyrows.photo_1': photoOneDef,
    'storyrows.photo_2': photoTwoDef,
    'storyrows.photo_3': photoThreeDef,
    'storyrows.cta_1': ctaOneDef,
    'storyrows.cta_2': ctaTwoDef,
    'storyrows.cta_3': ctaThreeDef,
  },
};

(StoryRows as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default StoryRows;
